import { useCallback, useRef, useState } from "react";
import { PROMPTS } from "../data/prompts";
import { getExampleImageIndexForPrompt } from "../data/promptExamplePhotos";
import { Button } from "./Button";
import { PromptCard } from "./PromptCard";
import {
  PromptImageStack,
  type PromptImageJump,
} from "./PromptImageStack";

/**
 * Same order as `/public/prompt-examples` and `PROMPT_EXAMPLE_IMAGE_LABELS` in `prompts.ts`.
 * Defined here so Bun dev HMR cannot leave an imported binding undefined at runtime.
 */
const EXAMPLE_STACK_PROMPT_LABELS: readonly string[] = [
  "a photo from the gc",
  "photo during in covid",
  "your first recent photo in your camera roll",
  "just woke up photo",
  "flower photos",
  "drafted instagram photos",
];

/** Includes every prompt that has a matching image in /public/prompt-examples. */
const FLOATING_STEPS = [0, 7, 14, 21, 40, 47, 49, 28];

const FLOAT_WRAPS = [
  "hidden left-[0%] top-[10%] w-[min(100%,14rem)] -rotate-6 sm:block sm:left-[2%] md:left-[4%]",
  "hidden right-[0%] top-[14%] w-[min(100%,14rem)] rotate-3 sm:block sm:right-[2%] md:right-[6%]",
  "left-[0%] top-[38%] hidden w-[min(100%,14rem)] -rotate-3 sm:left-[3%] sm:block md:top-[40%]",
  "right-[0%] top-[42%] hidden w-[min(100%,14rem)] rotate-6 sm:right-[4%] sm:block",
  "bottom-[28%] left-[2%] hidden w-[min(100%,14rem)] -rotate-12 md:bottom-[26%] md:left-[6%] md:block",
  "bottom-[20%] right-[2%] hidden w-[min(100%,14rem)] rotate-6 md:bottom-[18%] md:right-[8%] md:block",
  "top-[24%] left-[18%] hidden w-[min(100%,12rem)] rotate-2 lg:left-[12%] lg:block",
  "top-[20%] right-[14%] hidden w-[min(100%,12rem)] -rotate-2 lg:right-[10%] lg:block",
];

export function PromptsShowcaseSection() {
  const jumpSeq = useRef(0);
  const [jump, setJump] = useState<PromptImageJump | null>(null);
  const [stackFrontIndex, setStackFrontIndex] = useState(0);

  const promptsList = Array.isArray(PROMPTS) ? PROMPTS : [];

  const pickPromptForStack = useCallback((text: string) => {
    const index = getExampleImageIndexForPrompt(text);
    if (index === undefined) return;
    jumpSeq.current += 1;
    setJump({ id: jumpSeq.current, index });
  }, []);

  const clearJump = useCallback(() => setJump(null), []);

  const onStackFrontIndex = useCallback((index: number) => {
    setStackFrontIndex(index);
  }, []);

  const activeStackPrompt =
    EXAMPLE_STACK_PROMPT_LABELS[stackFrontIndex] ?? "";

  return (
    <section
      id="prompts"
      className="relative z-10 min-h-0 bg-gradient-to-b from-[#151519] to-[#0B1117] text-white sm:min-h-[min(100vh,1200px)]"
      aria-labelledby="prompts-showcase-heading"
    >
      <div className="relative mx-auto max-w-6xl px-4 pb-24 pt-8 sm:px-6 md:pt-12">
        <h2
          id="prompts-showcase-heading"
          className="sr-only"
        >
          Prompts in Photobomb
        </h2>

        <div className="relative min-h-[52vh] sm:min-h-[70vh]">
          {/* Scattered cards: phones only — avoids doubling "This round" on tablet/desktop */}
          <div className="pointer-events-none absolute inset-0 z-[1] block md:hidden">
            {FLOATING_STEPS.map((step, idx) => {
              const wrap = FLOAT_WRAPS[idx] ?? FLOAT_WRAPS[0];
              const promptText =
                promptsList[
                  promptsList.length ? step % promptsList.length : 0
                ] ?? "";
              const hasExample =
                getExampleImageIndexForPrompt(promptText) !== undefined;
              return (
                <div
                  key={`${step}-${idx}`}
                  className={`pointer-events-none absolute z-[1] ${wrap}`}
                >
                  <PromptCard
                    text={promptText}
                    className="pointer-events-auto scale-[0.92] shadow-black/35 sm:scale-100"
                    onActivate={
                      hasExample
                        ? () => pickPromptForStack(promptText)
                        : undefined
                    }
                  />
                </div>
              );
            })}
          </div>

          <div className="relative z-[2] flex min-h-[52vh] flex-col px-2 sm:min-h-[70vh]">
            <div className="mx-auto flex w-full max-w-lg shrink-0 flex-col items-center pt-4 sm:pt-10 md:pt-12">
              <Button
                type="button"
                variant="orange"
                pad="custom"
                tabIndex={-1}
                className="pointer-events-none max-w-md cursor-default px-6 py-3 text-center text-base font-semibold shadow-[0_12px_40px_rgba(0,0,0,0.35)] sm:px-7 sm:py-3.5 sm:text-lg"
                aria-label="100+ prompts every round"
              >
                100+ prompts every round.
              </Button>
              <p className="mt-3 max-w-md text-balance text-center text-sm leading-relaxed text-white/60 sm:mt-3.5 sm:text-base">
                Same energy as the app: snap, vote, roast gently.
              </p>
            </div>

            <div
              className="mx-auto mt-8 flex w-full max-w-md flex-col items-center sm:mt-10 md:max-w-lg"
              aria-live={activeStackPrompt ? "polite" : undefined}
            >
              {activeStackPrompt ? (
                <div
                  key={stackFrontIndex}
                  className="showcase-prompt-reveal w-full max-w-[min(100%,18rem)]"
                >
                  <PromptCard
                    text={activeStackPrompt}
                    className="w-full shadow-[0_16px_44px_rgba(0,0,0,0.38)] ring-1 ring-white/25"
                  />
                </div>
              ) : null}

              <div
                className={
                  activeStackPrompt
                    ? "mt-6 flex w-full flex-col items-center sm:mt-7"
                    : "flex w-full flex-col items-center py-4"
                }
              >
                <PromptImageStack
                  jump={jump}
                  onJumpHandled={clearJump}
                  onFrontIndexChange={onStackFrontIndex}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
